import React, { FC, useMemo, useState, useEffect } from 'react'
import { SxProps } from '@mui/system'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'

export const FaIconStringMap: Record<string, string> = {
  dashboard: 'fa-house',
  support: 'fa-comments-question-check',
  forum: 'fa-message-lines',
  faq: 'fa-list-check',
  check: 'fa-check',
  exclamation: 'fa-triangle-exclamation',
  opsmanual: 'fa-book-open-cover',
  partnerships: 'fa-handshake',
  marketing: 'fa-magnifying-glass-location',
  induction: 'fa-rocket-launch',
  reporting: 'fa-chart-line-up',
  email: 'fa-envelope',
  crm: 'fa-people-group',
  filestore: 'fa-hard-drive',
  network: 'fa-chart-network',
  settings: 'fa-sliders',
  payments: 'fa-piggy-bank',
  update: 'fa-newspaper',
  training: 'fa-graduation-cap',
  booking: 'fa-ballot-check',
  website: 'fa-laptop-code',
  notebook: 'fa-notebook',
  admin: 'fa-inboxes',
  star: 'fa-medal',
  covid: 'fa-face-mask',
  recruitment: 'fa-clipboard-list-check',
  events: 'fa-calendar-star',
  shop: 'fa-shopping-cart',
  login: 'fa-sign-in',
  logout: 'fa-sign-out',
  delete: 'fa-trash',
  copy: 'fa-copy',
  view: 'fa-eye',
  edit: 'fa-pencil',
  audit: 'fa-list-timeline',
  cancel: 'fa-xmark',
  upload: 'fa-upload',
  timeline: 'fa-timeline',
  diagramNested: 'fa-diagram-nested',
  users: 'fa-users',
  militaryPerson: 'fa-person-military-to-person',
  gear: 'fa-gear',
  personPortal: 'fa-person-to-portal',
  arrowTurnDownRight: 'fa-arrow-turn-down-right',
  search: 'fa-magnifying-glass',
  ellipsis: 'fa-ellipsis-vertical',
  plus: 'fa-plus',
  arrowUpDown: 'fa-arrow-up-arrow-down',
  messagePlus: 'fa-message-plus',
  folder: 'fa-folder',
  folderOpen: 'fa-folder-open',
  document: 'fa-file-invoice',
  books: 'fa-books',
  arrowUp: 'fa-arrow-up',
  arrowDown: 'fa-arrow-down',
  cog: 'fa-cog',
  connect: 'fa-signal',
}

export interface IconProps {
  id?: string,
  iconName: string,
  buttonVariant?: 'text' | 'outlined' | 'contained',
  color?: string,
  fontSize?: string,
  tooltip?: string,
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right',
  buttonSx?: SxProps,
  iconSx?: SxProps,
  onClick?: () => void,
}

const FAIcon: FC<React.PropsWithChildren<IconProps>> = ({
  id,
  iconName,
  buttonVariant='text',
  color='secondary.main',
  fontSize='1rem',
  tooltip,
  tooltipPlacement='top',
  iconSx = {},
  buttonSx = {},
  onClick,
  children,
}) => {
  const [ renderCount, setRenderCount ] = useState(0)
  const [ visible, setVisible ] = useState(true)
  const theme = useTheme()

  const useColor = useMemo(() => {
    if(color == 'primary.main') return theme.palette.primary.main
    if(color == 'secondary.main') return theme.palette.secondary.main
    return color
  }, [
    theme,
    color,
  ])

  // it seems font awesome non-react doesn't like changing the name of the icon very much
  // so if the icon name "changes" (i.e. not on the first render)
  // let's return null for 100ms to help font awesome catch up
  useEffect(() => {
    if(renderCount > 0) {
      setVisible(false)
      setTimeout(() => {
        setVisible(true)
      }, 100)
    }
    setRenderCount(c => c+1)
  }, [
    iconName,
  ])

  if(!visible) return
  
  const iconClassName = `fa-light ${FaIconStringMap[iconName] || iconName}`
  const iconElem = (
    <>
      <Box
        id={ id }
        component={'span'}
        sx={{
          color,
          fontSize,
          ...iconSx,
        }}
      >
        <i className={ iconClassName } style={{color: useColor}} />
      </Box>
      {
        children
      }
    </>
  )

  const button = onClick ? (
    <Button
      variant={ buttonVariant }
      component="div"
      onClick={ onClick }
      sx={ buttonSx }
    >
     { iconElem } 
    </Button>
  ) : iconElem

  return tooltip ? (
    <Tooltip
      title={ tooltip }
      placement={ tooltipPlacement }
    >
      { button }
    </Tooltip>
  ) : button
}

export default FAIcon